import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Building2,
  Camera,
  CheckCircle2,
  Clock,
  FileSignature,
  FileText,
  HardHat,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  Plus,
  Receipt,
  Send,
  Snowflake,
  Star,
  Thermometer,
  ThermometerSun,
  Trash2,
  Truck,
  UserMinus,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { Helper, PageHead, JobStatusBadge, PriorityFlag } from '../components/bits.jsx';
import { Avatar, Badge, Button, Callout, Card, CardBody, CardHead, Checkbox, cx, EmptyState, Field, Input, KeyValue, Loading, Modal, PhoneInput, Select, StarRating, Stars, Textarea, Timeline, useToast } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { dateLabel, dateTimeLabel, hours, JOB_STATUS, money, num, phone, phoneDial, relative, statusMeta, timeLabel, titleCase, untilLabel } from '../lib/format.js';
import { FREIGHT_PLAIN, JOB_NEXT_STEP, TEMP_PLAIN, WORK_PLAIN, countOf, dollars, jobStep, matchWords, whenPlain } from '../lib/plain.js';
import { durationPlain } from '../lib/plain.js';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/jobs/${id}/detail`, { deps: [id] });
  const [transition, setTransition] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const job = data?.job;

  const nextAction = job ? JOB_NEXT_STEP[job.status] : null;

  const doTransition = async (to, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/transition`, { to, ...extra });
      toast.ok(`Job is now “${jobStep(to).plain}”`);
      setTransition(null);
      refresh();
    } catch (err) {
      toast.err('Status change failed', err.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/jobs/${id}/notes`, { body: note.trim() });
      setNote('');
      refresh();
    } catch (err) {
      toast.err('Could not save the note', err.message);
    }
  };

  const unassign = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/unassign`, {});
      toast.info('Assignment removed — back on the board');
      refresh();
    } catch (err) {
      toast.err('Could not unassign', err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !job) {
    return (
      <>
        <PageHead title="Job" subtitle="Loading…" />
        <Loading rows={7} />
      </>
    );
  }
  if (!job) {
    return (
      <>
        <PageHead title="Job not found" />
        <Card>
          <EmptyState icon={FileText} title="We cannot find that job" action={<Link to="/app/jobs"><Button variant="primary" size="sm">Back to all jobs</Button></Link>} />
        </Card>
      </>
    );
  }

  const evidence = data?.evidence || [];
  const events = data?.events || [];
  const offers = data?.offers || [];
  const site = data?.site;
  const lumper = data?.lumper;
  const customer = data?.customer;
  const workers = data?.workers || [];
  const slots = data?.slots || { needed: 1, filled: 0, open: 1, full: false };
  const leadCrew = data?.leadCrew || [];
  const leadScheduleText = data?.leadScheduleText || null;

  const moneyRows = [
    ['We pay the lumper', dollars(job.lumperPay)],
    ['We bill the customer', dollars(job.customerBill)],
    ['Left over for us', dollars(job.margin)],
  ];

  return (
    <>
      <PageHead
        title={customer?.name ? `${customer.name} at ${site?.name || 'the dock'}` : job.code}
        breadcrumb={[{ label: 'All jobs', to: '/app/jobs' }, { label: `Job ${job.code.replace(/^JOB-/, '')}` }]}
        subtitle={
          <span className="row gap-10 wrap">
            <JobStatusBadge status={job.status} />
            <PriorityFlag priority={job.priority} />
            <span className="dim">Job {job.code.replace(/^JOB-/, '')}</span>
            <span className="dim">· {whenPlain(job.windowStart)}</span>
          </span>
        }
        actions={
          <>
            {job.status === 'draft' || job.status === 'posted' ? (
              <Button variant="outline" size="sm" icon={Send} onClick={async () => {
                try {
                  const payload = await api.post(`/dispatch/blast/${job.id}`, { top: 8 });
                  toast.ok(`Asked ${payload.data.offered} ${payload.data.offered === 1 ? 'person' : 'people'}`, 'They get a message on their phone straight away');
                  refresh();
                } catch (err) {
                  toast.err('Could not send the messages', err.message);
                }
              }}>
                Ask lumpers
              </Button>
            ) : null}
            <Button variant="outline" size="sm" icon={Pencil} onClick={() => setEditOpen(true)}>Change details</Button>
            <Button variant="outline" size="sm" icon={XCircle} onClick={() => setCancelOpen(true)}>Cancel this job</Button>
            {nextAction ? (
              <Button
                variant="primary"
                size="sm"
                icon={ArrowRight}
                loading={busy}
                onClick={() => {
                  if (nextAction.to === 'completed' || job.status === 'posted') setTransition(nextAction.to);
                  else doTransition(nextAction.to);
                }}
              >
                {nextAction.label}
              </Button>
            ) : null}
          </>
        }
      />

      {!job.lumperId && ['posted', 'draft'].includes(job.status) ? (
        <Callout
          tone={job.priority === 'hot' ? 'red' : 'amber'}
          icon={AlertTriangle}
          title={`Nobody booked yet · starts ${untilLabel(job.windowStart)}`}
          actions={
            <>
              <Button size="sm" variant="primary" icon={UserPlus} onClick={() => setAssignOpen(true)}>Find a lumper</Button>
              <Link to="/app/dispatch"><Button size="sm" variant="outline">See all jobs needing someone</Button></Link>
            </>
          }
          className="mb-20"
        >
          We will show you the closest people who are free and have done this kind of work before.
        </Callout>
      ) : null}

      <div className="split-wide">
        <div className="col gap-20">
          <Card>
            <CardHead title="What this job is" icon={FileText} />
            <CardBody>
              <div className="kv-grid">
                <div className="col gap-2">
                  <span className="label">In the trailer</span>
                  <span>{FREIGHT_PLAIN[job.freightType] || titleCase(job.freightType)}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{WORK_PLAIN[job.workType] || titleCase(job.workType)}</span>
                </div>
                <div className="col gap-2">
                  <span className="label">Trailer</span>
                  <span className="row gap-6">
                    {job.temperature === 'frozen' ? <Snowflake size={14} /> : job.temperature === 'reefer' ? <Thermometer size={14} /> : <ThermometerSun size={14} />}
                    {TEMP_PLAIN[job.temperature] || titleCase(job.temperature)}
                  </span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{job.commodity || 'No note about what is inside'}</span>
                </div>
                <div className="col gap-2">
                  <span className="label">Pallets</span>
                  <span className="num">
                    {job.palletsActual || '—'} counted, {job.palletsExpected} expected
                  </span>
                  {job.palletsActual && job.palletsActual !== job.palletsExpected ? (
                    <Badge tone="amber" size="sm">does not match by {job.palletsActual - job.palletsExpected}</Badge>
                  ) : null}
                </div>
                <div className="col gap-2">
                  <span className="label">Time it took</span>
                  <span className="num">{durationPlain(job.billableHours || job.estimatedHours)}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>we guessed {durationPlain(job.estimatedHours)}</span>
                </div>
                <div className="col gap-2">
                  <span className="label">Trailer number</span>
                  <span className="mono">{job.trailerNumber || '—'}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>Seal {job.sealNumber || '—'}</span>
                </div>
                <div className="col gap-2">
                  <span className="label">Paperwork numbers</span>
                  <span className="mono">{job.poNumber || '—'}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                    {job.soNumber ? `SO ${job.soNumber}` : 'No SO'} · {job.bolNumber ? `BOL ${job.bolNumber}` : 'No BOL'}
                  </span>
                </div>
                <div className="col gap-2">
                  <span className="label">Door</span>
                  <span className="num">{job.dockDoor || '—'}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{site?.dockHours || '—'}</span>
                </div>
                <div className="col gap-2">
                  <span className="label">Waiting time</span>
                  <span className="num">{job.waitMinutes ? `${job.waitMinutes} min` : '—'}</span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>usually {Math.round(Number(site?.avgWaitMinutes || 0))} min here</span>
                </div>
              </div>

              {job.instructions ? (
                <div className="tile mt-16">
                  <span className="label">What the lumper was told</span>
                  <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{job.instructions}</p>
                </div>
              ) : null}
              {job.notes ? (
                <div className="tile">
                  <span className="label">Our own notes</span>
                  <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{job.notes}</p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Photos and paperwork"
              subtitle={
                evidence.length
                  ? `${evidence.length} photo${evidence.length === 1 ? '' : 's'} sent in by the lumper`
                  : 'No photos sent in yet'
              }
              icon={Camera}
              actions={job.requireSignature ? <Badge tone="violet" size="sm" dot>Needs a signature</Badge> : null}
            />
            <CardBody>
              {evidence.length ? (
                <div className="evidence-grid">
                  {evidence.map((item) => (
                    <figure key={item.id} className="evidence" onClick={() => setLightbox(item)}>
                      <img src={item.url} alt={item.caption || item.kind} loading="lazy" />
                      <figcaption>
                        {item.caption || titleCase(item.kind)} · {relative(item.at)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Camera} title="No photos yet">
                  Lumpers send photos of the trailer, the pallet count and the signed paperwork from their phone.
                </EmptyState>
              )}
            </CardBody>
            <div className="card-foot">
              <div className="row gap-8">
                <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = async () => {
                        try {
                          await api.post(`/jobs/${id}/evidence`, { kind: 'photo', url: reader.result, caption: file.name });
                          toast.ok('Photo attached');
                          refresh();
                        } catch (err) {
                          toast.err('Upload failed', err.message);
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  Add a photo
                </label>
                <span className="dim" style={{ fontSize: 'var(--fs-sm)', paddingTop: 6 }}>
                  Anything you add here is marked as coming from the office.
                </span>
              </div>
            </div>
          </Card>

          <Card>
            <CardHead title="What has happened so far" subtitle="Newest first" icon={Clock} />
            <CardBody>
              <Timeline
                items={events
                  .slice()
                  .reverse()
                  .map((event) => ({
                    id: event.id,
                    strong: true,
                    tone: jobStep(event.toStatus || event.kind).tone,
                    title: jobStep(event.toStatus || event.kind).plain,
                    when: `${relative(event.at)} · ${timeLabel(event.at)}`,
                    body: (
                      <span>
                        {event.body}
                        <span className="dimmer"> — {event.actorName || 'System'}</span>
                        {event.lat && event.lng ? (
                          <span className="dimmer"> · geotagged {Number(event.lat).toFixed(3)}, {Number(event.lng).toFixed(3)}</span>
                        ) : null}
                      </span>
                    ),
                  }))}
              />
              <div className="divider" />
              <div className="row gap-8">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Write something down about this job…"
                  onKeyDown={(e) => e.key === 'Enter' && addNote()}
                />
                <Button variant="outline" icon={MessageSquare} onClick={addNote} disabled={!note.trim()}>Save note</Button>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="col gap-20">
          <Card hot={Number(job.margin) <= 0}>
            <CardHead title="What this job pays" icon={Banknote} />
            <CardBody>
              {moneyRows.map(([label, value]) => (
                <KeyValue key={label} label={label}>
                  <span style={{ color: label === 'Left over for us' ? (Number(job.margin) >= 0 ? 'var(--green)' : 'var(--red)') : undefined, fontWeight: label === 'Left over for us' ? 650 : 500 }}>
                    {value}
                  </span>
                </KeyValue>
              ))}
              <Helper>“Left over” is what stays with us after the lumper is paid.</Helper>
              <div className="divider" />
              <KeyValue label="How the pay works">
                ${num(job.palletRate, 2)} a pallet, or ${num(job.hourlyRate, 0)} an hour
              </KeyValue>
              {data?.payoutItems?.length ? (
                <KeyValue label="Payment to lumper">
                  <Badge tone={data.payoutItems[0].payoutStatus === 'paid' ? 'green' : 'amber'}>
                    {data.payoutItems[0].payoutStatus === 'paid' ? 'Paid' : 'Not paid yet'}
                    {data.payoutItems[0].payoutReference ? ` · ${data.payoutItems[0].payoutReference}` : ''}
                  </Badge>
                </KeyValue>
              ) : null}
              {data?.invoiceItems?.length ? (
                <KeyValue label="Bill to customer">
                  <Link to={`/app/invoices/${data.invoiceItems[0].invoiceId}`} className="row gap-6">
                    <Badge tone={data.invoiceItems[0].invoiceStatus === 'paid' ? 'green' : 'blue'}>
                      {data.invoiceItems[0].invoiceCode}
                    </Badge>
                    <Receipt size={13} />
                  </Link>
                </KeyValue>
              ) : null}
              {job.status === 'completed' || job.status === 'verified' ? (
                <div className="row gap-8 mt-12">
                  <Button size="sm" variant="outline" className="grow" onClick={() => doTransition('verified')} loading={busy}>
                    The count is right
                  </Button>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Who is doing it"
              icon={HardHat}
              actions={
                lumper ? (
                  <Button size="sm" variant="ghost" icon={UserMinus} onClick={unassign} disabled={busy}>Take them off</Button>
                ) : (
                  <Button size="sm" variant="outline" icon={UserPlus} onClick={() => setAssignOpen(true)}>Find someone</Button>
                )
              }
            />
            <CardBody>
              {lumper ? (
                <>
                  <div className="row gap-12">
                    <Avatar name={`${lumper.firstName} ${lumper.lastName}`} tone={lumper.photoColor} size="lg" />
                    <div className="grow truncate">
                      <Link to={`/app/lumpers/${lumper.id}`} style={{ fontWeight: 640 }}>{lumper.firstName} {lumper.lastName}</Link>
                      <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                        {lumper.code} · {titleCase(lumper.tier)}
                      </div>
                      <Stars value={lumper.rating} />
                    </div>
                  </div>
                  <div className="divider" />
                  <KeyValue label="Phone"><span className="mono">{phone(lumper.phone)}</span></KeyValue>
                  <KeyValue label="Jobs they have done">{num(lumper.jobsCompleted)}</KeyValue>
                  <KeyValue label="What we pay them">${num(lumper.payRate, 2)} {lumper.payType === 'per_pallet' ? 'a pallet' : lumper.payType === 'per_job' ? 'a job' : 'an hour'}</KeyValue>
                  <div className="row gap-8 mt-12">
                    <a href={`tel:${phoneDial(lumper.phone)}`} className="grow"><Button size="sm" variant="outline" icon={Phone} className="btn-block">Call</Button></a>
                    <Link to={`/app/lumpers/${lumper.id}`} className="grow"><Button size="sm" variant="outline" className="btn-block">See their record</Button></Link>
                  </div>
                </>
              ) : (
                <EmptyState icon={HardHat} title="Nobody booked yet">
                  Pick someone from the list of people who are free.
                </EmptyState>
              )}
            </CardBody>
          </Card>

          <JobRoster
            job={job}
            workers={workers}
            slots={slots}
            leadCrew={leadCrew}
            leadScheduleText={leadScheduleText}
            onChanged={refresh}
          />

          {lumper && ['completed', 'verified', 'paid', 'invoiced'].includes(job.status) ? (
            <Card>
              <CardHead
                title="How did the crew do?"
                subtitle={job.rating ? 'You can change this if the customer calls back' : 'Ask the customer, then put it in here'}
                icon={Star}
              />
              <CardBody>
                <RateCrew
                  jobId={job.id}
                  initialStars={job.rating || 5}
                  initialComment={job.ratingComment || ''}
                  onSaved={refresh}
                />
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHead
              title="Where it is"
              icon={Building2}
              actions={customer ? <Link to={`/app/customers/${customer.id}`}><Button size="sm" variant="ghost">Open</Button></Link> : null}
            />
            <CardBody>
              {customer ? (
                <>
                  <div style={{ fontWeight: 620 }}>{customer.name}</div>
                  <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                    {customer.code} · {customer.kind} · {customer.terms?.toUpperCase()}
                  </div>
                </>
              ) : null}
              <div className="divider" />
              {site ? (
                <>
                  <div className="row-top gap-8">
                    <MapPin size={14} style={{ color: 'var(--amber)', marginTop: 3 }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{site.name}</div>
                      <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                        {site.address}<br />{site.city}, {site.state} {site.zip}
                      </div>
                    </div>
                  </div>
                  <div className="divider" />
                  <KeyValue label="Open hours">{site.dockHours}</KeyValue>
                  <KeyValue label="How many doors">{site.dockDoors}</KeyValue>
                  <KeyValue label="Booking">{site.appointmentRequired ? 'Has to be booked in' : 'Can just turn up'}</KeyValue>
                  {site.contactPhone ? <KeyValue label="Site contact"><span className="mono">{phone(site.contactPhone)}</span></KeyValue> : null}
                  {site.checkInInstructions ? (
                    <div className="tile mt-12">
                      <span className="label">How to get in</span>
                      <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{site.checkInInstructions}</p>
                    </div>
                  ) : null}
                </>
              ) : (
                <span className="dim">No site on file.</span>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Who we asked" subtitle={offers.length ? `We have asked ${offers.length} ${offers.length === 1 ? 'person' : 'people'}` : 'Nobody asked yet'} icon={Send} />
            <CardBody className="col gap-10">
              {offers.length ? (
                offers.map((offer) => (
                  <div key={offer.id} className="row between gap-8">
                    <Link to={`/app/lumpers/${offer.lumperId}`} className="row gap-8 truncate">
                      <Avatar name={offer.lumperName} tone="blue" size="sm" />
                      <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{offer.lumperName}</span>
                    </Link>
                    <span className="row gap-8">
                      <span className="num dim" style={{ fontSize: 'var(--fs-xs)' }}>{money(offer.payOffer)}</span>
                      <Badge
                        size="sm"
                        tone={offer.status === 'accepted' ? 'green' : offer.status === 'declined' ? 'red' : 'muted'}
                      >
                        {offer.status}
                      </Badge>
                    </span>
                  </div>
                ))
              ) : (
                <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Nobody asked yet. Use “Ask lumpers” above to send it out.</span>
              )}
            </CardBody>
          </Card>

          {data?.disputes?.length ? (
            <Card>
              <CardHead title="Problems reported" icon={AlertTriangle} />
              <CardBody className="col gap-12">
                {data.disputes.map((dispute) => (
                  <div key={dispute.id} className="tile">
                    <div className="row between gap-8">
                      <Badge tone={dispute.status === 'open' ? 'red' : 'muted'} size="sm">{titleCase(dispute.category)}</Badge>
                      <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{money(dispute.amountClaimed)}</span>
                    </div>
                    <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>{dispute.body}</p>
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>

      <TransitionModal
        open={Boolean(transition)}
        to={transition}
        job={job}
        onClose={() => setTransition(null)}
        busy={busy}
        onSubmit={(extra) => doTransition(transition, extra)}
      />

      <AssignModal
        open={assignOpen}
        job={job}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => {
          setAssignOpen(false);
          refresh();
        }}
      />

      <CancelModal
        open={cancelOpen}
        job={job}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          refresh();
        }}
      />

      <EditModal
        open={editOpen}
        job={job}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          refresh();
        }}
      />

      <Modal open={Boolean(lightbox)} onClose={() => setLightbox(null)} size="wide" title={lightbox?.caption || 'Field capture'}>
        {lightbox ? (
          <>
            <img src={lightbox.url} alt={lightbox.caption || 'Field capture'} style={{ width: '100%', borderRadius: 'var(--r-md)' }} />
            <div className="row between mt-12">
              <Badge tone="violet">{titleCase(lightbox.kind)}</Badge>
              <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                captured {relative(lightbox.at)} by {lightbox.uploadedByType}
              </span>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
}

/* ----------------------------------------------------------------- modals */

function TransitionModal({ open, to, job, onClose, onSubmit, busy }) {
  const meta = to ? jobStep(to) : null;
  const [pallets, setPallets] = useState('');
  const [cases, setCases] = useState('');
  const [billableHours, setBillableHours] = useState('');
  const [wait, setWait] = useState('');
  const [rating, setRating] = useState(5);
  const [note, setNote] = useState('');

  const needsCounts = to === 'completed';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={meta ? `Mark this job as “${meta.plain}”` : ''}
      subtitle={job ? `${job.palletsExpected} pallets expected` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Never mind</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              onSubmit({
                note: note || undefined,
                ...(pallets ? { palletsActual: Number(pallets) } : {}),
                ...(cases ? { casesActual: Number(cases) } : {}),
                ...(billableHours ? { billableHours: Number(billableHours) } : {}),
                ...(wait ? { waitMinutes: Number(wait) } : {}),
                ...(needsCounts ? { rating } : {}),
              })
            }
          >
            Yes, save it
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        {needsCounts ? (
          <>
            <p className="muted">
              Tell us what really came off the trailer. This is what the lumper gets paid for and what we bill the
              customer, so it is worth getting right.
            </p>
            <div className="grid grid-2" style={{ gap: 14 }}>
              <Field label="How many pallets came off?" hint={`We expected ${job?.palletsExpected}`}>
                <Input className="mono" type="number" value={pallets} onChange={(e) => setPallets(e.target.value)} placeholder={String(job?.palletsExpected || '')} />
              </Field>
              <Field label="How many boxes? (optional)">
                <Input className="mono" type="number" value={cases} onChange={(e) => setCases(e.target.value)} placeholder={String(job?.casesExpected || '')} />
              </Field>
              <Field label="How many hours did it take?">
                <Input className="mono" type="number" step="0.25" value={billableHours} onChange={(e) => setBillableHours(e.target.value)} placeholder={String(job?.estimatedHours || '')} />
              </Field>
              <Field label="How long did they wait? (minutes)">
                <Input className="mono" type="number" value={wait} onChange={(e) => setWait(e.target.value)} placeholder="0" />
              </Field>
            </div>
            <Field label="How did they do?">
              <div className="row gap-12">
                <Stars value={rating} size={20} />
                <div className="row gap-6">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={cx('chip', rating === value && 'active')}
                      onClick={() => setRating(value)}
                    >
                      {value}★
                    </button>
                  ))}
                </div>
              </div>
            </Field>
          </>
        ) : null}
        <Field label="Anything to add? (optional)">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth writing down about this job" />
        </Field>
      </div>
    </Modal>
  );
}

/** The office rating the crew, usually right after the customer tells them. */
function RateCrew({ jobId, initialStars, initialComment, onSaved }) {
  const toast = useToast();
  const [stars, setStars] = useState(initialStars || 5);
  const [comment, setComment] = useState(initialComment || '');
  const [busy, setBusy] = useState(false);

  return (
    <div className="col gap-12">
      <div className="col gap-6" style={{ alignItems: 'center' }}>
        <StarRating value={stars} onChange={setStars} size={30} />
        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {['Poor', 'Not great', 'Fine', 'Good', 'Excellent'][stars - 1]}
        </span>
      </div>
      <Textarea
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What the customer said about them…"
      />
      <Button
        variant="primary"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.post(`/lumpers/ratings/${jobId}`, { stars, comment: comment || undefined });
            toast.ok('Rating saved', "It shows on the lumper's record straight away.");
            onSaved?.();
          } catch (err) {
            toast.err('Could not save the rating', err.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        Save rating
      </Button>
    </div>
  );
}

function AssignModal({ open, job, onClose, onAssigned }) {
  const toast = useToast();
  const { data: candidates, loading } = useApi(`/dispatch/candidates/${job?.id}`, { skip: !open });
  const [payOffer, setPayOffer] = useState('');
  const [busy, setBusy] = useState(false);

  const assign = async (lumperId) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/assign`, { lumperId, payOffer: payOffer ? Number(payOffer) : job.lumperPay });
      toast.ok('Job given out', 'They are booked in for this one');
      onAssigned();
    } catch (err) {
      toast.err('Could not give out the job', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Who should do this job?"
      subtitle={job ? `The best people for this job are at the top of the list` : ''}
      footer={<Button variant="ghost" onClick={onClose}>Done</Button>}
    >
      <Field label="What we will pay them" hint={`This job is priced at ${money(job?.lumperPay || 0)}`} className="mb-16">
        <Input className="mono" value={payOffer} onChange={(e) => setPayOffer(e.target.value)} placeholder={money(job?.lumperPay || 0)} />
      </Field>
      <div className="col gap-8">
        {loading && !candidates ? <Loading rows={4} /> : null}
        {(candidates || []).map((candidate) => (
          <div key={candidate.id} className="candidate">
            <Avatar name={candidate.name} tone={candidate.photoColor || 'amber'} />
            <div className="grow truncate">
              <div className="row gap-6">
                <span className="truncate" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{candidate.name}</span>
                {candidate.tier !== 'standard' ? <Badge tone="teal" size="sm">{titleCase(candidate.tier)}</Badge> : null}
              </div>
              <span className="reason truncate">{candidate.reasons.join(' · ')}</span>
            </div>
            <span className="num" style={{ minWidth: 46, textAlign: 'right' }}>{candidate.score}</span>
            <Badge tone={matchWords(candidate.score).tone} size="sm">{matchWords(candidate.score).label}</Badge>
            <Button size="sm" variant="primary" disabled={busy} onClick={() => assign(candidate.id)}>Give it to them</Button>
          </div>
        ))}
        {candidates && !candidates.length ? (
          <EmptyState icon={HardHat} title="Nobody free close by">
            Try asking further away from the jobs needing someone screen.
          </EmptyState>
        ) : null}
      </div>
    </Modal>
  );
}

function CancelModal({ open, job, onClose, onCancelled }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancel this job?"
      subtitle={job ? `Job ${job.code.replace(/^JOB-/, '')} will be marked as not happening.` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Leave it alone</Button>
          <Button
            variant="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/jobs/${job.id}/transition`, { to: 'cancelled', note: reason || 'Cancelled by dispatch' });
                toast.info('Job cancelled', 'It will not show up as work to be done.');
                onCancelled();
              } catch (err) {
                toast.err('Could not cancel', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Yes, cancel it
          </Button>
        </>
      }
    >
      <Field label="Why is it being cancelled?" hint="This is saved on the job so everyone can see what happened.">
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The trailer is not coming today…" />
      </Field>
    </Modal>
  );
}

function EditModal({ open, job, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const current = useMemo(
    () => ({
      palletsExpected: job?.palletsExpected ?? '',
      estimatedHours: job?.estimatedHours ?? '',
      lumperPay: job?.lumperPay ?? '',
      customerBill: job?.customerBill ?? '',
      dockDoor: job?.dockDoor ?? '',
      priority: job?.priority ?? 'standard',
      notes: job?.notes ?? '',
      peopleNeeded: job?.peopleNeeded ?? 1,
      openToAnyone: !!job?.openToAnyone,
    }),
    [job],
  );

  const value = (key) => (form[key] !== undefined ? form[key] : current[key]);
  const set = (key, next) => setForm((prev) => ({ ...prev, [key]: next }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Change the details"
      subtitle={job ? `Job ${job.code.replace(/^JOB-/, '')}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Never mind</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const patch = {};
                Object.entries(form).forEach(([key, next]) => {
                  if (next !== '' && next !== undefined) patch[key] = ['palletsExpected', 'estimatedHours', 'lumperPay', 'customerBill', 'peopleNeeded'].includes(key) ? Number(next) : next;
                });
                await api.patch(`/jobs/${job.id}`, patch);
                toast.ok('Saved');
                onSaved();
              } catch (err) {
                toast.err('Could not save', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="How many pallets?">
            <Input className="mono" type="number" value={value('palletsExpected')} onChange={(e) => set('palletsExpected', e.target.value)} />
          </Field>
          <Field label="How long will it take?" hint="In hours — half hours are fine">
            <Input className="mono" type="number" step="0.5" value={value('estimatedHours')} onChange={(e) => set('estimatedHours', e.target.value)} />
          </Field>
          <Field label="What we pay the lumper">
            <Input className="mono" type="number" value={value('lumperPay')} onChange={(e) => set('lumperPay', e.target.value)} />
          </Field>
          <Field label="What we bill the customer">
            <Input className="mono" type="number" value={value('customerBill')} onChange={(e) => set('customerBill', e.target.value)} />
          </Field>
          <Field label="Door number">
            <Input value={value('dockDoor')} onChange={(e) => set('dockDoor', e.target.value)} />
          </Field>
          <Field label="How many people does this take?" hint="One person is normal. Put 4 for a job that needs a crew.">
            <Input className="mono" type="number" min="1" max="12" value={value('peopleNeeded')} onChange={(e) => set('peopleNeeded', e.target.value)} />
          </Field>
          <Field label="How urgent is it?">
            <Select
              value={value('priority')}
              onChange={(e) => set('priority', e.target.value)}
              options={[
                { value: 'standard', label: 'Normal' },
                { value: 'hot', label: 'Rush — needed right away' },
                { value: 'low', label: 'Not urgent' },
              ]}
            />
          </Field>
        </div>
        <Field label="Our own notes">
          <Textarea rows={3} value={value('notes')} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        <Field label="Who can take this job" hint="Leave this off for jobs that only go to the lumpers we send them to.">
          <Checkbox
            checked={value('openToAnyone')}
            onChange={(e) => set('openToAnyone', e.target.checked)}
            label="Any lumper nearby can take an open spot"
          />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Who is on this job.
 *
 * A job can take more than one person. The lead is the one we hold responsible
 * and the one we pay; the men he brings are listed here so the office knows who
 * is on the dock, and so the ones with the app can be paid and rated later.
 */
function JobRoster({ job, workers = [], slots, leadCrew = [], leadScheduleText, onChanged }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);

  const addWorker = async (body, label) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/workers`, body);
      toast.ok(label);
      onChanged();
    } catch (err) {
      toast.err('Could not add him', err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeWorker = async (worker) => {
    try {
      await api.del(`/jobs/${job.id}/workers/${worker.id}`);
      toast.ok(`${worker.name} taken off this job`);
      onChanged();
    } catch (err) {
      toast.err('Could not take him off', err.message);
    }
  };

  const notOnYet = leadCrew.filter((member) => !member.onThisJob);

  return (
    <Card>
      <CardHead
        title="Who is on this job"
        subtitle={
          slots.needed > 1
            ? `This job takes ${countOf(slots.needed, 'person')} — ${slots.filled} sorted, ${slots.open} still to find`
            : 'This job takes one person'
        }
        icon={Users}
        actions={
          slots.needed > 1 ? (
            <Badge tone={slots.full ? 'green' : 'amber'} size="sm" dot>
              {slots.filled} of {slots.needed} on it
            </Badge>
          ) : null
        }
      />
      <CardBody>
        {leadScheduleText ? (
          <Callout tone="blue" icon={Clock} className="mb-16">
            His crew normally works {leadScheduleText}.
          </Callout>
        ) : null}

        {workers.length ? (
          <div className="col gap-10 mb-16">
            {workers.map((worker) => (
              <div key={worker.id} className="row gap-10" style={{ alignItems: 'center' }}>
                <Avatar name={worker.name} size="sm" tone={worker.role === 'lead' ? 'amber' : 'muted'} />
                <div className="col gap-2 grow truncate">
                  <span className="row gap-6 wrap">
                    <span style={{ fontWeight: 600 }}>{worker.name}</span>
                    {worker.role === 'lead' ? (
                      <Badge tone="amber" size="sm">In charge</Badge>
                    ) : (
                      <Badge tone="muted" size="sm">Helping</Badge>
                    )}
                    {worker.source === 'claimed' ? <Badge tone="teal" size="sm" dot>Picked up a spot</Badge> : null}
                    {!worker.lumperId ? <Badge tone="blue" size="sm">Not signed up yet</Badge> : null}
                  </span>
                  <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                    {worker.phone ? `${phone(worker.phone)} · ` : ''}
                    {worker.leadId && worker.role !== 'lead' ? 'One of the lead\u2019s crew' : ''}
                  </span>
                </div>
                {worker.role === 'lead' ? null : (
                  <Button size="sm" variant="ghost" icon={Trash2} onClick={() => removeWorker(worker)} disabled={busy}>
                    Take off
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="muted mb-16" style={{ fontSize: 'var(--fs-sm)' }}>
            Nobody is on this job yet.
          </p>
        )}

        {notOnYet.length ? (
          <>
            <div className="label mb-8">Men on the lead&rsquo;s own crew</div>
            <div className="row gap-6 wrap mb-16">
              {notOnYet.map((member) => (
                <Button
                  key={member.id}
                  size="sm"
                  variant="outline"
                  icon={Plus}
                  disabled={busy || slots.open < 1}
                  onClick={() => addWorker({ crewMemberId: member.id }, `${member.name} put on this job`)}
                >
                  {member.name}
                </Button>
              ))}
            </div>
          </>
        ) : null}

        <div className="grid grid-3" style={{ gap: 12, alignItems: 'end' }}>
          <Field label="Add somebody by name">
            <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Walk-on Willie" />
          </Field>
          <Field label="His phone number (optional)">
            <PhoneInput value={form.phone} onChange={(value) => setForm((p) => ({ ...p, phone: value }))} placeholder="(305) 123-4567" />
          </Field>
          <Button
            variant="outline"
            icon={UserPlus}
            loading={busy}
            disabled={slots.open < 1 && slots.needed > 1}
            onClick={async () => {
              if (!form.name.trim()) {
                toast.err('We need a name', 'Type in who is going, even if it is just a first name.');
                return;
              }
              await addWorker({ name: form.name.trim(), phone: form.phone.trim() || undefined }, `${form.name} put on this job`);
              setForm({ name: '', phone: '' });
            }}
          >
            Put him on the job
          </Button>
        </div>

        <div className="divider" />
        <div className="row gap-10" style={{ alignItems: 'center' }}>
          <Badge tone={job.openToAnyone ? 'teal' : 'muted'} size="sm" dot>
            {job.openToAnyone ? 'Anyone nearby can take a spot' : 'Only people we send it to'}
          </Badge>
          <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
            {job.openToAnyone
              ? 'Lumpers who are free can take an open spot on this job from their phone. We still see every name here.'
              : 'This job only goes to the lumpers we send it to.'}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}
